import { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { useTranslation } from "react-i18next";
import {
  formattedDate,
  formattedTime,
} from "../formatted/FormattedDateAndTime";
import useWindowDimensions from "../formatted/UseWindowsDimensions";

/**
 * Bar chart showing the filling-level gain (%) achieved by each individual
 * compaction event, in chronological order (oldest to newest).
 */
export const CompactionEfficiencyChart = ({ compactionPairs }) => {
  const { t } = useTranslation();
  const { heightWindow } = useWindowDimensions();
  const chartHeight = Math.max(220, Math.min(320, heightWindow / 3));
  const chartDomRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    if (!chartDomRef.current) return;

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartDomRef.current);
    }
    const chart = chartInstanceRef.current;

    // Pairs are stored newest-first; the chart reads left-to-right in time.
    const chronological = [...compactionPairs].reverse();

    const option = {
      grid: {
        left: 8,
        right: 16,
        top: 24,
        bottom: 8,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          const point = chronological[params[0].dataIndex];
          if (!point) return "";
          return `${t("Compaction")} n°${point.order} - ${formattedDate(point.after._time, t)}, ${formattedTime(point.after._time)}<br/>${point.gain}% ${t("Filling level gain")}`;
        },
      },
      xAxis: {
        type: "category",
        data: chronological.map((point) => `#${point.order}`),
        axisLine: { lineStyle: { color: "#E5E7EB" } },
        axisTick: { show: false },
        axisLabel: { color: "#9CA3AF", fontSize: 11 },
      },
      yAxis: {
        type: "value",
        axisLabel: { formatter: "{value}%", color: "#9CA3AF", fontSize: 11 },
        splitLine: { lineStyle: { color: "#F1F5F9" } },
      },
      series: [
        {
          type: "bar",
          data: chronological.map((point) => point.gain ?? 0),
          barMaxWidth: 28,
          itemStyle: {
            color: "#00A7A6",
            borderRadius: [4, 4, 0, 0],
          },
          label: {
            show: true,
            position: "top",
            formatter: "{c}%",
            color: "#222222",
            fontSize: 11,
            fontWeight: 600,
          },
        },
      ],
    };

    chart.setOption(option, true);
    chart.resize();

    const resizeFn = () => chart.resize();
    window.addEventListener("resize", resizeFn);
    return () => window.removeEventListener("resize", resizeFn);
  }, [compactionPairs, t]);

  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div ref={chartDomRef} style={{ width: "100%", height: chartHeight }} />
  );
};
